import React, { useMemo } from 'react';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import classes from '@nerdwallet/base-styles/classes';
import classNames from 'classnames';
import { getEditingConfig } from './universal-container-editing-config';
import BackgroundColor from '../background-color/background-color';

import styles from './universal-container.module.less';
import {
  universalContainerDefaultProps,
  universalContainerPropTypes,
} from './universal-container-utils';

const DefaultContent = () => <h1>Default Content</h1>;
/**
 * TODO: implement all params doc/types
 * @param {{style: React.CSSProperties}}
 * @returns
 */
const UniversalContainer = ({
  children,
  backgroundImageDesktop,
  backgroundImageTablet,
  backgroundImageMobile,
  size,
  backgroundColor: bgColor,
  hexColor,
  backgroundSize,
  padding,
  margin,
  border,
  width,
  alignment,
  hide,
  style,
  jumpLinkId,
  scrollOffsetDesktop,
  scrollOffsetTablet,
  scrollOffsetMobile,
  backgroundPositionX,
  backgroundPositionY,
  parallaxOpacity,
  parallaxForMobile,
  containerHeightDesktop,
  containerHeightMobile,
  useParallax,
  ...rest
}) => {
  const backgroundImageSrcMap = useMemo(
    () => ({
      lg: backgroundImageDesktop,
      md: backgroundImageTablet,
      sm: backgroundImageMobile,
    }),
    [backgroundImageDesktop, backgroundImageMobile, backgroundImageTablet]
  );

  const {
    borderColor,
    borderWidth,
    shadowColor,
    type: borderType,
    borderRounded,
  } = border;

  const borderColorStyle =
    borderType === 'color'
      ? {
          border: `${borderWidth || 0}px solid ${borderColor || '#cdcdcd'}`,
        }
      : null;

  const borderShadowStyle =
    borderType === 'shadow' && shadowColor
      ? {
          boxShadow: `0px 1px 10px -1px ${shadowColor}`,
        }
      : null;

  const borderRadiusStyle =
    borderType !== 'none' &&
    borderRounded?.type === 'custom' &&
    borderRounded?.value
      ? {
          borderRadius: borderRounded?.value,
        }
      : null;

  const backgroundImage = useMemo(() => {
    if (backgroundImageSrcMap[size]) {
      return `url(${backgroundImageSrcMap[size]})`;
    }

    if (backgroundImageSrcMap.lg) {
      return `url(${backgroundImageSrcMap.lg})`;
    }

    if (backgroundImageSrcMap.lg) {
      return `url(${backgroundImageSrcMap.md})`;
    }

    return null;
  }, [backgroundImageSrcMap, size]);

  const bgOptions = { backgroundColor: bgColor, hexColor };

  const applyCurrencyRoundedBorders =
    borderType !== 'none' &&
    (borderRounded?.type === 'currency' || 'currency') &&
    (borderRounded?.currencyValue || 'border-radius-1');

  const paddingType = padding?.type || 'currency';
  const marginType = margin?.type || 'currency';
  const widthType = width?.type || 'currency';

  const getWidth = () => {
    const sizes = Object.entries(width?.screenSizes || {})?.reduce(
      (acc, [key, value]) => (value ? [...acc, key] : acc),
      []
    );

    if (!sizes.length && widthType) {
      return {
        width: width?.[widthType]?.value,
      };
    }

    return {
      ...(sizes?.length &&
        sizes?.includes(size) && {
          width: width?.[widthType]?.value,
        }),
    };
  };

  const getHideSize = () => {
    const sizes = Object.entries(hide || {})?.reduce(
      (acc, [key, value]) => (value ? [...acc, key] : acc),
      []
    );
    return {
      ...(sizes.length && sizes.includes(size) && { display: 'none' }),
    };
  };

  const scrollMargin = useMemo(
    () =>
      jumpLinkId &&
      ({
        lg: scrollOffsetDesktop,
        md: scrollOffsetTablet,
        sm: scrollOffsetMobile,
      }[size] ??
        scrollOffsetDesktop ??
        scrollOffsetTablet ??
        scrollOffsetMobile ??
        0),
    [
      size,
      jumpLinkId,
      scrollOffsetDesktop,
      scrollOffsetTablet,
      scrollOffsetMobile,
    ]
  );

  const getMargin = () => {
    if (margin?.type === 'custom') {
      return {
        marginTop: Number(margin?.[marginType]?.top),
        marginRight: Number(margin?.[marginType]?.right),
        marginBottom: Number(margin?.[marginType]?.bottom),
        marginLeft: Number(margin?.[marginType]?.left),
      };
    }
    return {
      marginTop: margin?.[marginType]?.top,
      marginRight: margin?.[marginType]?.right,
      marginBottom: margin?.[marginType]?.bottom,
      marginLeft: margin?.[marginType]?.left,
    };
  };

  const backgroundSizeStyle = useParallax
    ? {
        backgroundSize,
        boxShadow: parallaxOpacity,
      }
    : { backgroundSize };

  const containerHeightValue =
    parallaxForMobile && size !== 'lg'
      ? containerHeightMobile
      : containerHeightDesktop;

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <div
          id={jumpLinkId}
          className={classNames('container', [
            styles.container,
            {
              [classes(borderRounded?.currencyValue)]:
                applyCurrencyRoundedBorders,
              [classes('display-flex')]: alignment?.type === 'flex',
              [styles.parallaxStyles]: useParallax,
            },
          ])}
          style={{
            ...getHideSize(),
            ...getWidth(),
            ...(useParallax && { height: containerHeightValue }),
            ...backgroundSizeStyle,
            ...(alignment?.direction &&
              alignment?.type === 'flex' && {
                flexDirection: alignment?.direction,
              }),
            ...(alignment?.horizontal &&
              alignment?.type === 'flex' && {
                alignItems: alignment?.horizontal,
              }),
            ...(alignment?.vertical &&
              alignment?.type === 'flex' && {
                justifyContent: alignment?.vertical,
              }),
            backgroundImage,
            backgroundColor,
            paddingTop: padding?.[paddingType]?.top,
            paddingRight: padding?.[paddingType]?.right,
            paddingBottom: padding?.[paddingType]?.bottom,
            paddingLeft: padding?.[paddingType]?.left,
            marginTop: getMargin(margin).marginTop,
            marginRight: getMargin(margin).marginRight,
            marginBottom: getMargin(margin).marginBottom,
            marginLeft: getMargin(margin).marginLeft,
            backgroundPositionX,
            backgroundPositionY,
            scrollMarginTop: `${scrollMargin || 0}px`,
            ...borderColorStyle,
            ...borderShadowStyle,
            ...borderRadiusStyle,
            ...style,
          }}
          {...rest}
        >
          {children || <DefaultContent />}
        </div>
      )}
    </BackgroundColor>
  );
};

UniversalContainer.displayName = 'Universal Container';
UniversalContainer.defaultProps = universalContainerDefaultProps;
UniversalContainer.propTypes = universalContainerPropTypes;
UniversalContainer.getEditingConfig = getEditingConfig;

export default connect(
  createStructuredSelector({
    size: ({ mediaQuery }) => mediaQuery,
  })
)(UniversalContainer);
